import { cn } from "@/components/podkit/lib/cn";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/podkit/tabs/Tabs";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Flags } from "@/feature-flags";
import { useContextURL } from "@/hooks/use-context-url";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useEnvironmentClassEntries } from "@/hooks/use-environment-class-entries";
import { useFlag } from "@/hooks/use-flag";
import { useRequireProject } from "@/hooks/use-require-project";
import { TrackLocations } from "@/hooks/use-segment";
import { Authentication } from "@/routes/environments/create/components/Authentication";
import {
    TabsValues,
    type CreateEnvironmentStage,
    type TabValue,
} from "@/routes/environments/create/create-environment-utils";
import { useMemo, useState, type FC } from "react";
import { NoEnvModal } from "./components/NoEnvModal";
import { BlankTab } from "./tabs/BlankTab";
import { ProjectTab } from "./tabs/ProjectTab";
import { RepositoryTab } from "./tabs/RepositoryTab";

export const CreateEnvironmentDetailsLayout: FC<{
    /**
     * CreateProjectModal uses this layout to create a project.
     * We don't want to show the project selection and blank environment tabs in this case.
     */
    disableProjectSelection?: boolean;
    onClose: () => void;
    initialContextURL: string;
}> = ({ onClose, initialContextURL, disableProjectSelection = false }) => {
    useDocumentTitle("Create Environment");

    // is this member allowed to create environments from non-projects?
    const { requireProjects } = useRequireProject();
    const { value: allowCreationOfBlankEnvironments } = useFlag(Flags.AllowCreationOfBlankEnvironments, false);

    const { contextURL, contextURLValid, setContextURL } = useContextURL(initialContextURL); // this serves the RepositoryTab as the controlled input value. We store it here because changing tabs re-renders the RepositoryTab, and we don't want to lose the input value when changing tabs or when authentication is required

    const initialTab = useMemo(() => {
        if (contextURLValid) {
            return TabsValues.url;
        }
        if (disableProjectSelection) {
            return TabsValues.url;
        }

        return TabsValues.project;
    }, [contextURLValid, disableProjectSelection]);
    const [tab, setTab] = useState<TabValue>(); // we don't initialize the tab with `initialTab`, because it might take a render or two for contextURL to be available

    const [stage, setStage] = useState<CreateEnvironmentStage>({ type: "input" });
    const { environmentClassEntries, isLoading: isLoadingEnvClasses } = useEnvironmentClassEntries();
    const hasNoEnvClasses = !isLoadingEnvClasses && environmentClassEntries.length === 0;
    if (hasNoEnvClasses) {
        return <NoEnvModal onClose={onClose} />;
    }

    if (stage.type === "scm-authentication") {
        return (
            <div className="flex size-full flex-col items-center justify-center gap-4 transition-height duration-500">
                <Authentication
                    repoURL={stage.contextUrl}
                    authenticationUrl={stage.authenticationUrl}
                    patAuth={stage.patAuth}
                    scmId={stage.scmId}
                    scmName={stage.scmName}
                    onClose={onClose}
                    onClickContinue={() => {
                        setStage({ type: "input" });
                        if (stage.onClickContinue) {
                            stage.onClickContinue();
                        }
                    }}
                    runnerId={stage.runner.runnerId}
                    data-track-location={TrackLocations.CreateEnvironmentSCMAuthenticationModal}
                />
            </div>
        );
    }

    const showFromProjectTab = !disableProjectSelection;
    const showFromScratchTab = allowCreationOfBlankEnvironments && !requireProjects && !disableProjectSelection;

    return (
        <div
            className="flex h-auto flex-col gap-4 px-5 py-4 transition-height duration-500"
            data-testid="create-environment-form"
        >
            <div className="flex flex-col items-center justify-start">
                <Heading1>New environment</Heading1>
            </div>
            <Tabs onValueChange={(v) => setTab(v as TabValue)} value={tab ?? initialTab}>
                <div className="flex flex-col items-center justify-start">
                    <TabsList
                        className={cn("inline-flex w-auto max-w-full flex-row gap-2 overflow-x-auto", {
                            hidden: !(showFromProjectTab || showFromScratchTab), // if we'd only show the url tab, we can hide the tabs list
                        })}
                    >
                        {showFromProjectTab && (
                            <TabsTrigger
                                className="flex-shrink-0 flex-grow-0"
                                value={TabsValues.project}
                                data-testid="from-project-tab"
                            >
                                From a Project
                            </TabsTrigger>
                        )}
                        <TabsTrigger
                            className="flex-shrink-0 flex-grow-0"
                            value={TabsValues.url}
                            data-testid="from-url-tab"
                        >
                            From a URL
                        </TabsTrigger>
                        {showFromScratchTab && (
                            <TabsTrigger
                                className="flex-shrink-0 flex-grow-0"
                                value={TabsValues.scratch}
                                data-testid="from-scratch-tab"
                            >
                                From Scratch
                            </TabsTrigger>
                        )}
                    </TabsList>
                </div>
                {/* 
                    We set tabIndex to -1 to prevent the tabs themselves as being focus targets. This becomes an issue when the user tabs through the page, as the expectation is to land on an element inside the tab – such as the context URL input of RepositoryTab or the first project card of ProjectTab.
                    See https://www.w3.org/WAI/ARIA/apg/patterns/tabs/examples/tabs-manual/#accessibilityfeatures for why tabs are focusable by default.
                    This is not fully addressed by our approach here, since it's not always the case that there is a focusable element inside the tab – like when the user is creating a blank environment and the class list is still loading.
                 */}
                <TabsContent value={TabsValues.project} tabIndex={-1}>
                    <ProjectTab onClose={onClose} />
                </TabsContent>
                <TabsContent value={TabsValues.url} tabIndex={-1}>
                    <RepositoryTab
                        contextURL={contextURL}
                        contextURLValid={contextURLValid}
                        setContextURL={setContextURL}
                        onClose={onClose}
                        setStage={setStage}
                        disableProjectSelection={disableProjectSelection}
                    />
                </TabsContent>
                <TabsContent value={TabsValues.scratch} tabIndex={-1}>
                    <BlankTab onClose={onClose} />
                </TabsContent>
            </Tabs>
        </div>
    );
};
